<template>
  <!-- Ablage beim Versenden (Kundenfeedback 24.10.2026, IV.2): ob die E-Mail
       und/oder ihre Anhänge ZUSÄTZLICH in einem Ordner der Akte gespeichert
       werden. In der Korrespondenz der Akte steht die E-Mail ohnehin
       (Kundenfeedback 26.10.2026, VII: Begriffe; gewählte Ordner-Ablage blau
       wie andere eingeschaltete Knöpfe, z. B. „Signatur“). -->
  <v-menu v-model="open" :close-on-content-click="false" location="top start" offset="6">
    <template #activator="{ props: activator }">
      <button
        v-bind="activator"
        :aria-label="`${ctl.summary.value}. ${ctl.tooltip.value}`"
        class="sfc__trigger"
        :class="{ 'sfc__trigger--on': filed }"
        data-testid="send-filing-trigger"
        :title="ctl.tooltip.value"
        type="button"
      >
        <FolderInput :size="16" />
        <span class="sfc__label">{{ ctl.summary.value }}</span>
        <ChevronDown :size="13" />
      </button>
    </template>
    <section :aria-label="$gt('Zusätzlich in einem Ordner der Akte speichern?')" class="sfc" data-testid="send-filing-menu">
      <header class="sfc__head">
        <h3 class="sfc__title">{{ $gt('Zusätzlich in einem Ordner der Akte speichern?') }}</h3>
        <ChatTip
          location="top"
          :text="$gt('Die E-Mail steht nach dem Senden immer in der Korrespondenz der Akte. Hier wählen Sie, ob sie (als PDF) und ihre Anhänge außerdem als Dokument in einem Ordner der Akte gespeichert werden.')"
          :title="$gt('Korrespondenz und Ordner')"
        >
          <span class="sfc__info" data-testid="send-filing-info" tabindex="0"><Info :size="14" /></span>
        </ChatTip>
      </header>
      <div class="sfc__modes" role="radiogroup">
        <button
          :aria-checked="!filedMode ? 'true' : 'false'"
          class="sfc__mode"
          :class="{ 'sfc__mode--on': !filedMode }"
          data-testid="send-filing-none"
          role="radio"
          type="button"
          @click="ctl.update({ mode: 'none' })"
        >
          {{ $gt('Nein') }}
        </button>
        <button
          :aria-checked="filedMode ? 'true' : 'false'"
          class="sfc__mode"
          :class="{ 'sfc__mode--on': filedMode }"
          data-testid="send-filing-folder"
          role="radio"
          type="button"
          @click="ctl.update({ mode: 'folder' })"
        >
          {{ $gt('In Ordner speichern') }}
        </button>
      </div>

      <template v-if="filedMode">
        <CaseFolderSelect
          :groups="ctl.folders.groups.value"
          :label="$gt('Ordner')"
          :loading="ctl.folders.loading.value"
          :model-value="ctl.choice.value.folder"
          @update:model-value="ctl.update({ folder: $event })"
        />
        <p v-if="ctl.folders.error.value" class="sfc__error" role="alert">{{ ctl.folders.error.value }}</p>
        <label class="sfc__check">
          <input :checked="ctl.choice.value.withMail" type="checkbox" @change="ctl.update({ withMail: ($event.target as HTMLInputElement).checked })">
          <span>{{ $gt('E-Mail als PDF') }}</span>
        </label>
        <label class="sfc__check" :class="{ 'sfc__check--off': attachmentCount === 0 }">
          <input
            :checked="ctl.choice.value.withAttachments"
            :disabled="attachmentCount === 0"
            type="checkbox"
            @change="ctl.update({ withAttachments: ($event.target as HTMLInputElement).checked })"
          >
          <span>
            {{ $gt('Anhänge als Dokumente') }}
            <em>({{ attachmentCount === 0 ? $gt('keine Anhänge') : attachmentCount }})</em>
          </span>
        </label>
      </template>
      <p v-if="ctl.autoFile.value" class="sfc__hint">
        {{ $gt('Vorbelegt nach der Einstellung Ihrer Organisation (gesendete E-Mails automatisch in einem Ordner speichern).') }}
      </p>
      <footer class="sfc__foot">
        <button class="sfc__done" data-testid="send-filing-apply" type="button" @click="open = false">{{ $gt('Übernehmen') }}</button>
      </footer>
    </section>
  </v-menu>
</template>

<script setup lang="ts">
  import type { SendFilingController } from './useSendFiling'
  import { ChevronDown, FolderInput, Info } from 'lucide-vue-next'
  import { computed, ref } from 'vue'
  import CaseFolderSelect from '../chat/CaseFolderSelect.vue'
  import ChatTip from '../chat/ChatTip.vue'

  const props = defineProps<{ ctl: SendFilingController, attachmentCount: number }>()

  const open = ref(false)
  const filedMode = computed(() => props.ctl.choice.value.mode === 'folder')
  const filed = computed(() => props.ctl.plan.value.action !== 'none')
</script>

<style scoped>
.sfc__trigger {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 32px; max-width: 300px; padding: 0 10px;
  border: 1px solid var(--border-grey-color); border-radius: 8px;
  background: #fff; color: var(--grey-color);
  font-size: 12px; line-height: 1; white-space: nowrap; cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.sfc__trigger:hover { background: var(--grey-light-color); color: var(--dark-color); }
.sfc__trigger:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 1px; }
/* Ordner-Ablage gewählt: blau mit weißer Schrift wie „Signatur“ (VII). */
.sfc__trigger--on,
.sfc__trigger--on:hover { border-color: var(--primary-color); background: var(--primary-color); color: #fff; }
.sfc__label { min-width: 0; overflow: hidden; text-overflow: ellipsis; }

.sfc {
  display: flex; flex-direction: column; gap: 10px;
  width: 340px; padding: 14px;
  border: 1px solid var(--border-grey-color); border-radius: 12px;
  background: #fff; box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
}
.sfc__head { display: flex; align-items: center; gap: 6px; }
.sfc__title { margin: 0; font-size: 0.86rem; font-weight: 700; color: var(--dark-color); }
.sfc__info { display: inline-flex; color: var(--grey-color); cursor: help; border-radius: 50%; }
.sfc__info:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 1px; }
.sfc__modes { display: flex; gap: 6px; }
.sfc__mode {
  flex: 1; padding: 7px 10px;
  border: 1px solid var(--border-grey-color); border-radius: 999px;
  background: #fff; color: var(--dark-color); font-size: 0.8rem; font-weight: 600; cursor: pointer;
}
.sfc__mode:hover { border-color: var(--primary-color); }
.sfc__mode:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.sfc__mode--on { border-color: var(--primary-color); background: var(--primary-color); color: #fff; }
.sfc__check { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; color: var(--dark-color); cursor: pointer; }
.sfc__check em { font-style: normal; color: var(--grey-color); }
.sfc__check--off { opacity: 0.6; cursor: default; }
.sfc__hint { margin: 0; font-size: 0.74rem; color: var(--grey-color); }
.sfc__error { margin: 0; font-size: 0.76rem; color: #c62828; }
.sfc__foot { display: flex; justify-content: flex-end; }
.sfc__done {
  padding: 6px 14px; border: 1px solid var(--primary-color); border-radius: 8px;
  background: var(--primary-color); color: #fff; font-size: 0.8rem; font-weight: 600; cursor: pointer;
}
</style>
